import { PublicKey } from "@solana/web3.js";
import BN from "bn.js";
import Decimal from "decimal.js";

import { filterUndefined } from "./primitives";
import {
    DISABLED_APY,
    DISABLED_APY_BIGINT,
    DOT,
    EMPTY_STRING,
    PERCENT_DECIMALS,
    STRICT_INTEGER_ONLY_REGEX,
    TRAILING_ZEROS_REGEX,
    ZERO
} from "../constants";

/** USD minimum expressed in a token, ceiled at the 4th decimal so entering the displayed amount clears the gate. */
export function minInputInToken(minUsd: number, usdPrice: number): number {
    return Math.ceil((minUsd / usdPrice) * 10_000) / 10_000;
}

export function lamportsToUiAmount(rawAmount: number | undefined = 0, decimals: number | undefined) {
    if (!decimals) return rawAmount;
    const result = rawAmount / Math.pow(10, decimals);
    const fixedResult = result.toFixed(decimals);
    return parseFloat(fixedResult);
}
export function bigIntLamportsToUiAmount(rawAmount: bigint | undefined, decimals: number | undefined): string {
    if (rawAmount === undefined) return ZERO;
    if (!decimals) return rawAmount.toString();

    const base = BigInt(10) ** BigInt(decimals);

    const whole = rawAmount / base;
    const fraction = rawAmount % base;

    if (fraction === BigInt(0)) return whole.toString();

    const fractionStr = fraction.toString().padStart(decimals, ZERO).replace(TRAILING_ZEROS_REGEX, EMPTY_STRING);

    return `${whole}.${fractionStr}`;
}

export function stringLamportsToUiAmountDecimal(rawAmount: string, decimals: number | undefined): string {
    if (!decimals || !rawAmount) return rawAmount;
    return new Decimal(rawAmount).div(new Decimal(10).pow(decimals)).toString();
}
export function decimalLamportsToUiAmount(
    rawAmount: Decimal | null | undefined,
    decimals: number | undefined
): Decimal | null {
    if (rawAmount === null || !decimals || rawAmount === undefined) return null;
    return new Decimal(rawAmount).div(new Decimal(10).pow(decimals));
}

export function stringLamportsToUiAmount(rawAmount: string | undefined, decimals: number | undefined) {
    return lamportsToUiAmount(rawAmount !== undefined ? parseInt(rawAmount) : undefined, decimals);
}

export function bigNumberStringToUiAmount(rawAmount: string | undefined, decimals: number | undefined) {
    return lamportsToUiAmount(rawAmount !== undefined ? new BN(rawAmount, "hex").toNumber() : undefined, decimals);
}

export enum RoundingType {
    UP = "UP",
    DOWN = "DOWN",
    ROUND = "ROUND"
}

export function uiAmountToLamports(
    uiAmount: number | undefined = 0,
    decimals: number | undefined,
    roundType: RoundingType = RoundingType.ROUND
) {
    if (!decimals) return uiAmount;
    const rawAmount = uiAmount * Math.pow(10, decimals);

    switch (roundType) {
        case RoundingType.UP:
            return Math.abs(Math.ceil(rawAmount));
        case RoundingType.DOWN:
            return Math.abs(Math.floor(rawAmount));
        default:
            return Math.abs(Math.round(rawAmount));
    }
}

export function uiPercentToBps(percent: number | undefined = 0, type: "cbps" | "bps" = "cbps") {
    return decimalsToBps(percentUiToDecimals(percent), type);
}

export function decimalsToBps(percent: number | undefined = 0, type: "cbps" | "bps" = "cbps") {
    const bpsDecimals = type === "cbps" ? 4 : 2;
    return Math.round(percent * 100 * 10 ** bpsDecimals);
}

export function bpsToPercent(bps: number | undefined = 0) {
    return percentDecimalsToUi(bpsToUiDecimals(bps));
}

export function bpsToUiDecimals(bps: number | undefined = 0, type: "cbps" | "bps" = "cbps") {
    const bpsDecimals = type === "cbps" ? 4 : 2;

    return bps / (100 * 10 ** bpsDecimals);
}

/**
 * Converts integers stored in hundredths of the UI unit: divide by 100.
 * Examples: basis points → percent scalar (5000 → 50); `leverageX100` 150 → 1.5.
 * (Distinct from {@link bpsToPercent} / {@link bpsToUiDecimals}, which use the cbps decimal pipeline.)
 */
export function perHundred(value: number): number {
    return value / 100;
}

export function perHundredOptional(value: number | null | undefined): number | null {
    if (value == null) return null;
    return perHundred(value);
}

// Converts a 10% input of 0.1 -> 10
export function percentDecimalsToUi(percentDecimals: number | undefined = 0) {
    if (percentDecimals <= 0) return percentDecimals;
    return roundAutoToDecimals(percentDecimals * 100, PERCENT_DECIMALS);
}

// Converts a 10% input of 0.1 -> 10
export function percentUiToDecimals(percentDecimals: number | undefined = 0) {
    return percentDecimals / 100;
}

export function multiplierFactorToPercent(multiplierFactor: number | undefined = 0) {
    return multiplierFactor * 100;
}

export function stringUiPercentToDecimals(percentString: string | undefined) {
    return percentUiToDecimals(percentString !== undefined ? parseFloat(percentString) : undefined);
}

export function stringUiAmountToLamports(uiAmount: string | undefined, decimals: number | undefined) {
    return uiAmountToLamports(uiAmount !== undefined ? parseFloat(uiAmount) : undefined, decimals);
}

export function decimalUiAmountToStringLamports(uiAmount: string | undefined | null, decimals: number | undefined) {
    if (!uiAmount || !decimals || uiAmount === null) return DISABLED_APY;
    return new Decimal(uiAmount).mul(new Decimal(10).pow(decimals)).toString();
}

function isValidFiniteNumberString(val: string | null | undefined): boolean {
    if (typeof val !== "string") return false;
    const trimmed = val.trim();
    if (trimmed === "" || trimmed === ".") return false;
    const num = Number(trimmed);
    if (!Number.isFinite(num)) return false;
    return true;
}

export function decimalStringUiPercentToBps(percent: string | null | undefined, type: "cbps" | "bps" = "cbps"): string {
    if (!percent || !isValidFiniteNumberString(percent)) return DISABLED_APY;

    const percentDecimal = new Decimal(percent);
    const bpsDecimals = type === "cbps" ? 4 : 2;
    return percentDecimal
        .div(100)
        .mul(100 * 10 ** bpsDecimals)
        .toString();
}

export function decimalPercentToUi(decimalPercent: Decimal | null | undefined): string | null {
    if (!decimalPercent) return null;
    return decimalPercent.mul(100).toString();
}

export function sumArray<T>(array: T[] | undefined, propertyKey: keyof T) {
    if (!array) {
        return 0;
    }
    return array.reduce((prev, curr) => {
        const currVal = typeof curr[propertyKey] === "number" ? (curr[propertyKey] as number) : 0;
        return prev + currVal;
    }, 0);
}

export function findMinElement<T>(array: T[] | undefined, mapToNum: (e: T) => number | undefined): T | undefined {
    if (!array) {
        return undefined;
    }

    let minElement: T | undefined = undefined;
    let minVal: number | undefined = undefined;
    array.forEach((e) => {
        const val = mapToNum(e);
        if (val !== undefined && (!minVal || val < minVal)) {
            minVal = val;
            minElement = e;
        }
    });

    return minElement;
}

export function findMaxElement<T>(array: T[] | undefined, mapToNum: (e: T) => number | undefined): T | undefined {
    if (!array) {
        return undefined;
    }

    let minElement: T | undefined = undefined;
    let minVal: number | undefined = undefined;
    array.forEach((e) => {
        const val = mapToNum(e);
        if (val && (!minVal || val > minVal)) {
            minVal = val;
            minElement = e;
        }
    });

    return minElement;
}

export function findHighestOccurringElement<T extends number | string>(array: T[]) {
    const occurrencesMap = array.reduce((map, curr) => {
        const prev = map.get(curr) ?? 0;
        map.set(curr, prev + 1);
        return map;
    }, new Map<T, number>());

    let maxElement: T | undefined = undefined;
    let maxOccurrences = 0;

    for (const [element, occurrences] of occurrencesMap.entries()) {
        if (occurrences > maxOccurrences) {
            maxElement = element;
            maxOccurrences = occurrences;
        }
    }
    return { element: maxElement ?? array[0], occurrences: maxOccurrences };
}

export function findMinMax<T>(
    array: T[] | undefined,
    mapToNum: (e: T) => number | undefined
): { min: number | undefined; max: number | undefined } {
    const mappedToNum = array?.map((e) => mapToNum(e)).filter(filterUndefined);
    if (!mappedToNum?.length) {
        return { min: undefined, max: undefined };
    }

    const min = Math.min(...mappedToNum);
    const max = Math.max(...mappedToNum);

    return { min, max };
}

function add(...items: (number | undefined)[]) {
    return items.reduce((prev, curr) => (prev || 0) + (curr || 0), 0);
}

function sub(a: number | undefined, b: number | undefined) {
    if (a === undefined || b === undefined) return undefined;
    return a - b;
}

function tokenAdd(a: number | undefined, b: number | undefined, decimals: number | undefined) {
    const [aVal, bVal] = [uiAmountToLamports(a, decimals), uiAmountToLamports(b, decimals)];

    return lamportsToUiAmount(aVal + bVal, decimals);
}

function tokenSub(a: number | undefined, b: number | undefined, decimals: number | undefined) {
    const [aVal, bVal] = [uiAmountToLamports(a, decimals), uiAmountToLamports(b, decimals)];

    return lamportsToUiAmount(aVal - bVal, decimals);
}

function tokenMul(a: number | undefined, b: number | undefined, decimals: number | undefined) {
    const [aVal, bVal] = [uiAmountToLamports(a, decimals), uiAmountToLamports(b, decimals)];

    return lamportsToUiAmount(aVal * bVal, decimals);
}

function tokenAMul(tokenAmount: number | undefined, num: number | undefined, decimals: number | undefined) {
    if (!num) return 0;
    return lamportsToUiAmount(uiAmountToLamports(tokenAmount, decimals) * num, decimals);
}

function tokenDiv(a: number | undefined, b: number | undefined, decimals: number | undefined) {
    const [aVal, bVal] = [uiAmountToLamports(a, decimals), uiAmountToLamports(b, decimals)];

    if (!bVal) return 0;
    return lamportsToUiAmount(aVal / (bVal || 1), decimals);
}

function tokenADiv(tokenAmount: number | undefined, num: number | undefined, decimals: number | undefined) {
    if (!num) return 0;
    return lamportsToUiAmount(uiAmountToLamports(tokenAmount, decimals) / num, decimals);
}

function mul(...items: (number | undefined | null)[]): number | undefined {
    if (items.some((item) => item === undefined)) return undefined;
    return items.reduce((prev, curr) => (prev ?? 0) * (curr || 0), 1) ?? undefined;
}

function div(a: number | undefined, b: number | undefined) {
    return (a || 0) / (b || 1);
}

export const bsMath = { add, mul, sub, div, tokenSub, tokenAdd, tokenAMul, tokenMul, tokenDiv, tokenADiv };

export function createPubkey(pubkeyStr: string | undefined) {
    if (!pubkeyStr) return PublicKey.default;
    return new PublicKey(pubkeyStr);
}

export function roundToDecimals(val: number, decimals = 6) {
    if (val === 0) return 0;
    const divider = 10 ** decimals;
    return Math.ceil((val + Number.EPSILON) * divider) / divider;
}

export function roundDownToDecimals(val: number, decimals = 6) {
    const divider = 10 ** decimals;
    return Math.floor((val + Number.EPSILON) * divider) / divider;
}

export function roundDownToDecimalsAllowUndefined(val: number | undefined, decimals: number | undefined) {
    if (val === undefined || decimals === undefined) {
        return undefined;
    }
    return roundDownToDecimals(val, decimals);
}

export function roundAutoToDecimals(val: number, decimals = 6) {
    const divider = 10 ** decimals;
    return Math.round((val + Number.EPSILON) * divider) / divider;
}

/**
 * @returns a is greater than b
 */
export function greaterThan(a: number | undefined, b: number | undefined) {
    if (a === undefined || b === undefined) return false;
    return a > b;
}

/**
 * @returns a is greater than b
 */
export function greaterThanEqual(a: number | undefined, b: number | undefined) {
    if (a === undefined || b === undefined) return false;
    return a >= b;
}

/**
 * @returns a is less than b
 */
export function lessThan(a: number | undefined, b: number | undefined) {
    if (a === undefined || b === undefined) return false;
    return a < b;
}

export function calculatePercentChange(originalValue: number, newValue: number): number {
    if (!originalValue) return 0;

    const change = newValue - originalValue;
    const percentChange = change / originalValue;
    return percentChange;
}

/**BigInts are stored in their raw amounts */
export function bigIntAmountTag(rawAmount: bigint | undefined, decimals: number | undefined): bigint {
    if (!decimals) return rawAmount ?? BigInt(0);
    if (rawAmount === DISABLED_APY_BIGINT) return DISABLED_APY_BIGINT;
    if (rawAmount === undefined) return BigInt(0);
    return rawAmount;
}

/**BigInts are stored in their raw amounts */
export function bigIntPercentTag(rawAmount: bigint | undefined): bigint {
    if (rawAmount === DISABLED_APY_BIGINT) return DISABLED_APY_BIGINT;
    if (rawAmount === undefined) return BigInt(0);
    return rawAmount;
}

//** Converts big ints to basis points */
export function bigIntBpsToUiDecimals(bps: bigint | undefined = BigInt(0), type: "cbps" | "bps" = "cbps") {
    if (bps === DISABLED_APY_BIGINT) return DISABLED_APY_BIGINT;
    return bps;
}

//** Converts a string percent to a bigint */
export function stringUiPercentToBigInt(percentString: string | undefined): bigint {
    if (percentString === undefined || percentString.trim() === EMPTY_STRING) return DISABLED_APY_BIGINT;
    const percent = parseFloat(percentString.trim());
    if (Number.isNaN(percent)) return BigInt(0);
    return BigInt(Math.round(percent * 10_000));
}

export function stringAmountToBigInt(amount: string | undefined, decimals: number | undefined): bigint {
    if (amount === undefined || amount.trim() === EMPTY_STRING) {
        return DISABLED_APY_BIGINT;
    }

    if (decimals === undefined) return BigInt(0);

    const trimmed = amount.trim();

    if (!STRICT_INTEGER_ONLY_REGEX.test(trimmed)) {
        return BigInt(0);
    }

    const [wholePart, fractionPart = EMPTY_STRING] = trimmed.split(DOT);
    const base = BigInt(10) ** BigInt(decimals);

    const whole = BigInt(wholePart) * base;

    const paddedFraction =
        fractionPart.length >= decimals ? fractionPart.slice(0, decimals) : fractionPart.padEnd(decimals, ZERO);

    const fraction = BigInt(paddedFraction || ZERO);

    return whole + fraction;
}
// Helper function to convert a bigint to a number, if the bigint is too large, return Number.MAX_SAFE_INTEGER
export function safeBigIntToNumber(val: bigint): number {
    return val > BigInt(Number.MAX_SAFE_INTEGER) ? Number.MAX_SAFE_INTEGER : Number(val);
}

export function bigIntMin(a: bigint, b: bigint): bigint {
    return a < b ? a : b;
}
